import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

const EARTH_TEXTURE = '/earth.jpg'; // Local file in public folder

const GlobeSimple: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<any>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    // Scene
    const scene = new THREE.Scene();

    // Camera
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 2000);
    camera.position.z = 350;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    containerRef.current.appendChild(renderer.domElement);

    // OrbitControls
    controlsRef.current = new OrbitControls(camera, renderer.domElement);
    controlsRef.current.enableDamping = true;
    controlsRef.current.dampingFactor = 0.08;
    controlsRef.current.enablePan = false;
    controlsRef.current.minDistance = 200;
    controlsRef.current.maxDistance = 600;
    controlsRef.current.autoRotate = true;
    controlsRef.current.autoRotateSpeed = 0.4;

    // Earth Sphere
    const radius = 120;
    const segments = 64;
    const geometry = new THREE.SphereGeometry(radius, segments, segments);
    const textureLoader = new THREE.TextureLoader();
    textureLoader.load(EARTH_TEXTURE, (earthTexture) => {
      const material = new THREE.MeshPhongMaterial({ map: earthTexture });
      const earth = new THREE.Mesh(geometry, material);
      scene.add(earth);
    });

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
    directionalLight.position.set(5, 3, 5);
    scene.add(directionalLight);

    // Animation loop
    let animationId: number;
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      controlsRef.current?.update();
      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationId) cancelAnimationFrame(animationId);
      if (renderer && containerRef.current && renderer.domElement.parentNode === containerRef.current) {
        containerRef.current.removeChild(renderer.domElement);
      }
      controlsRef.current?.dispose();
    };
  }, []);

  return (
    <div className="relative w-full h-full min-h-[300px] min-w-[300px] flex items-center justify-center">
      <div ref={containerRef} className="w-full h-full rounded-xl z-10 relative min-h-[300px] min-w-[300px] bg-transparent" />
    </div>
  );
};

export default GlobeSimple; 